import { Property } from 'csstype';
import { Convention } from '../../../concepts/objects';
import { Enum } from '../../../platform/Enum';
import { Colors } from '../../../ui';
import { Physical } from './Physical';

export const TextAlignment = Enum({
    start: 0 as const,
    middle: 1 as const,
    end: 2 as const,
});

export type TextAlignment = typeof TextAlignment[keyof typeof TextAlignment];

export const textAlignmentToTextAlign = (a: TextAlignment): Property.TextAlign => {
    if (a === TextAlignment.start) return 'left';
    if (a === TextAlignment.end) return 'right';
    if (a === TextAlignment.middle) return 'center';
    return 'left';
};

export const textAlignmentToFlexAlign = (a: TextAlignment): Property.AlignItems => {
    if (a === TextAlignment.start) return 'flex-start';
    if (a === TextAlignment.middle) return 'center';
    if (a === TextAlignment.end) return 'flex-end';
    return 'flex-start';
};

export type TextMargin = number | number[];
export const TextMargin = {
    toPx(tm: TextMargin): string {
        if (!Array.isArray(tm)) tm = [tm, tm, tm, tm];
        if (tm.length === 1) tm = [tm[0], tm[0], tm[0], tm[0]];
        if (tm.length === 2) tm = [tm[0], tm[1], tm[0], tm[1]];
        if (tm.length === 3) tm = [tm[0], tm[1], tm[2], tm[1]];
        return tm.map(x => `${x}px`).join(' ');
    }
};

export interface TextSettings {

    fontFamily: string;

    fontColor: string;

    fontSize: number;

    fontStyle: string;

    fontWeight: number;

    lineHeight: number;

    maxFontSize: number;

    textMargin: TextMargin;

    textAlignH: TextAlignment;

    textAlignV: TextAlignment;
}

export const TextSettings = {
    default: Object.freeze({
        fontFamily: 'Rubik',
        fontColor: Colors.text,
        fontSize: 16,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 1.25,
        maxFontSize: null,
        textAlignV: TextAlignment.start,
        textAlignH: TextAlignment.start,
        textMargin: 2,
    }),
    from(ts: TextSettings) {
        // In some instances the passed in object will have non-enumerable properties (some fields have this) and it
        // causes issues so just copy everything explicitly to a new object.  Not doing this causes TextRuler to sometimes not work.
        return {
            fontFamily: ts.fontFamily,
            fontColor: ts.fontColor,
            fontSize: ts.fontSize,
            fontStyle: ts.fontStyle,
            fontWeight: ts.fontWeight,
            lineHeight: ts.lineHeight,
            maxFontSize: ts.maxFontSize,
            textAlignV: ts.textAlignV,
            textMargin: ts.textMargin,
            textAlignH: ts.textAlignH,
        };
    },
    toCSS(ts: TextSettings) {
        return {
            fontFamily: ts.fontFamily,
            color: ts.fontColor,
            fontSize: `${ts.fontSize}px`,
            fontStyle: ts.fontStyle,
            fontWeight: ts.fontWeight,
            lineHeight: `${ts.lineHeight}em`,
            margin: TextMargin.toPx(ts.textMargin),
        };
    }
};

export const HasText = new Convention<HasText>('HasText');

export interface HasText extends Physical, TextSettings {

    readonly width: number;

    readonly height: number;
}

export const PreferredTextColor = new Convention<PreferredTextColor>('PreferredTextColor');

export interface PreferredTextColor extends Physical {

    getPreferredTextColor(): string;
}
